import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alerta, Botao, Cartao, Nota, SeletorMultiplo } from "@plantus/ui";
import type { OpcaoDoSeletor } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type { Lote, VolumeDaEtiqueta } from "@plantus/api-client";

import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { Etiqueta } from "./Etiqueta";
import { escreverEscolha, interpretarEscolha } from "./escolhaDeVolumes";

/**
 * Quantas etiquetas cabem numa folha A4 — **duas colunas de 88 mm por duas
 * fileiras de 132 mm**.
 *
 * <p>A conta que produz este número está escrita por extenso em `app.css`, ao
 * lado da grade de impressão: 2 × 88 + 3 de goteira = 179 mm ≤ 194 de papel
 * útil; 2 × 132 + 3 = 267 mm ≤ 270,7 (os 281 mm de altura útil menos os 10,3 mm
 * que a marca de ambiente ocupa). **Conferido no PDF**: 20 etiquetas saem em
 * **5 páginas A4**, quatro por página.
 *
 * <p>⚠️ Ele existe para a MINIATURA saber onde uma folha acaba e a próxima
 * começa. Quem pagina de verdade é a impressora, pela grade de milímetros do
 * `@media print` — este número **descreve** aquela grade, não a substitui. Se
 * um dia a grade mudar, é aqui e lá que se mexe, e o comentário de `app.css`
 * cita este arquivo de volta.
 */
export const POR_FOLHA = 4;

/**
 * Reparte as etiquetas escolhidas em folhas de papel.
 *
 * <p>Não é uma segunda renderização: as etiquetas são **as mesmas**, e o que
 * este agrupamento produz é só o `<div>` de papel em volta de cada quatro. No
 * `@media print` esse `<div>` vira `display: contents` e some da caixa, e a
 * folha volta a ser a grade contínua de 88 mm × 2 que a impressora sempre
 * recebeu.
 */
export function repartirEmFolhas<T>(itens: readonly T[], porFolha = POR_FOLHA): T[][] {
  const folhas: T[][] = [];
  for (let i = 0; i < itens.length; i += porFolha) folhas.push(itens.slice(i, i + porFolha));
  return folhas;
}

/**
 * A prévia nasce **aberta no PC** e **recolhida no telefone**.
 *
 * <p>🔴 Era a pendência que a #36 deixou para o PO, com o número medido: com
 * vinte volumes escolhidos, a aba passava de **1 579 px para 11 939 px** de
 * altura em 360 px, e a prévia empilhada ficava ~10 300 px abaixo do botão que
 * ela ilustra. A miniatura corta isso, e não o zera — quem está no telefone
 * está conferindo um tambor, não imprimindo.
 *
 * <p>⚠️ **Isto é `@media`, e a grade de duas colunas é `@container` — e a
 * diferença não é descuido.** A pergunta *"cabem duas colunas?"* se responde
 * pela largura do PAINEL, e é por isso que ela é uma consulta de contêiner
 * (`docs/frontend/responsividade.md`). A pergunta daqui é outra: *"isto é um
 * telefone?"* — há dedo, a tela é curta e a navegação já virou gaveta. Essa é
 * uma pergunta sobre o **aparelho**, e a consulta de mídia é a ferramenta
 * certa para ela. O ponto é o mesmo `56rem` em que a lateral vira gaveta,
 * porque é a mesma fronteira.
 *
 * <p>⚠️ **R27 continua de pé:** o PO pediu a prévia visível na #36 e ela
 * continua visível — onde ela cabe. O que mudou é o telefone, que é
 * exatamente o caso que a pendência dele nomeava.
 *
 * <p>Sem `matchMedia` (jsdom, renderização no servidor) a resposta é **aberta**:
 * o padrão cai para o lado que o PO pediu.
 */
export function previaNasceAberta(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(min-width: 56rem)").matches;
}

/**
 * A folha de etiquetas, pronta para imprimir.
 *
 * <p>Formato <b>A6 retrato</b> — 100 × 150 mm. Numa A4 cabem <b>quatro</b>
 * (duas colunas × duas fileiras, a 92 % do tamanho nominal: a conta está
 * comentada em {@code app.css}, ao lado da grade). Quem recebe um lote em
 * vinte tambores precisa de vinte etiquetas iguais, e a folha resolve isso sem
 * equipamento novo.
 *
 * <p>A impressão é do navegador ({@code window.print}) com {@code @media
 * print}. O <b>registro</b> dela é do servidor: reimprimir é normal —
 * etiqueta rasga, molha, cai atrás do tambor — mas reimprimir sem rastro é como
 * material identificado errado sobrevive a uma conferência.
 *
 * <h2>🔴 A tela perguntava QUANTAS e imprimia as PRIMEIRAS N — #34</h2>
 *
 * <p>Queixa do PO: <i>"o ideal é que eu possa escolher qual etiqueta eu vou
 * imprimir. Eu posso querer imprimir só a etiqueta do volume 17 e não ter que
 * imprimir todas as 16 etiquetas anteriores para imprimir a 17."</i>
 *
 * <p>O defeito era uma linha — {@code volumes.slice(0, vias)} —, e o custo dela
 * não é papel. Cada etiqueta que sai vira uma <b>emissão</b>, registro
 * <b>imutável</b> por gatilho de banco (V11): imprimir de 1 a 17 para chegar no
 * 17 gravava <b>dezesseis emissões que ninguém pediu</b>, que não saem mais e
 * que a reconciliação de rótulos do Art. 200 passa a contar como material
 * aplicado. Some a isso dezesseis adesivos a mais circulando, que são dezesseis
 * chances de um tambor receber a etiqueta errada.
 *
 * <p>O servidor já resolvia desde a #25 — a rota recebe {@code {volumes, motivo}}
 * e {@code GET /lotes/{id}/volumes} devolve {@code viasEmitidas} por
 * recipiente. O que faltava era só a tela.
 *
 * <p>Hoje a escolha tem <b>três entradas para o mesmo estado</b>: o campo de
 * faixa (<code>17</code>, <code>3-7</code>, <code>1, 5, 9-12</code>), os
 * atalhos e a grade de números — e ela é <b>uma só</b>. Tocar um número
 * reescreve o campo; escrever a faixa marca os números. Um campo que só serve
 * de entrada vira mentira assim que alguém toca num tambor pelo dedo.
 *
 * <h2>A tela é uma CONFERÊNCIA, não um botão</h2>
 *
 * <p>Ela era um link "voltar", um campo "Vias", um botão e um alerta,
 * empilhados sem hierarquia, com a etiqueta solta embaixo. Quem chegava aqui
 * não via de qual lote, de qual material, de qual <b>empresa</b> nem em que
 * <b>situação</b> estava o material que ia sair colado num tambor — e esta é a
 * última tela antes de o documento existir em papel.
 *
 * <p>Hoje são duas peças, nesta ordem e por este motivo:
 *
 * <ol>
 *   <li>a <b>ficha</b> diz o que vai para o papel: matéria-prima, fornecedor,
 *       validade, quanto do lote já foi etiquetado — e, abaixo do filete,
 *       <b>quais</b> volumes vão;</li>
 *   <li>a <b>prévia</b> mostra a folha do jeito que sai.</li>
 * </ol>
 *
 * <h2>🔴 Ela deixou de ser uma PÁGINA e virou uma ABA do lote — #36</h2>
 *
 * <p>Queixa do PO: a tela <i>"está mal pensada de UI/UX"</i>, e o que ele pediu
 * foram <b>abas — do lote</b>. Resumo, volumes e etiquetas eram três páginas
 * separadas, com ida e volta pela lista. Hoje o lote abre com abas e esta é uma
 * delas ({@code LoteEmAbas}).
 *
 * <p>Duas coisas saíram daqui, e as duas <b>subiram</b> em vez de sumir:
 *
 * <ul>
 *   <li>a <b>cabeça da tela</b> (chapéu, {@code h1}, "voltar") — hoje é o
 *       cabeçalho do LOTE, acima das abas, e vale em todas elas;</li>
 *   <li>os <b>dois selos</b> (empresa e situação) — foram para o mesmo
 *       cabeçalho, e passaram a estar visíveis em toda aba em vez de só nesta.
 *       ADR-0011 §4 e ADR-0005 §10 continuam atendidos, um degrau acima.</li>
 * </ul>
 *
 * <h2>🔴 A prévia voltou a abrir VISÍVEL — e a #34 não foi apagada</h2>
 *
 * <p>Na #34 o PO pediu a prévia <b>sob demanda</b>, e foi entregue assim: a
 * tela abria mostrando a conferência, e a folha vinha a um toque. Na #36 ele
 * pediu o contrário, olhando a mesma tela já dentro das abas.
 *
 * <p><b>Não é a decisão anterior desfeita por descuido — é o arranjo que
 * mudou.</b> Com a conferência à esquerda e a folha à direita, "como vai sair"
 * deixou de ser uma pergunta que se faz depois de escolher e passou a ser parte
 * da própria conferência: escolher o tambor 17 e ver a etiqueta do 17 aparecer
 * é a mesma leitura, não duas. Empilhada numa coluna só, a prévia era um
 * segundo assunto no fim da página, e por isso a decisão da #34 estava certa
 * para a tela que existia então.
 *
 * <p>O botão de recolher <b>continua</b>, pelo motivo que criou a #34: vinte
 * etiquetas A6 com QR são a peça mais pesada da tela. O que mudou foi o lado
 * padrão. As duas decisões estão registradas lado a lado na feature 1.2.1.
 *
 * <h2>🔴 A cabeça era uma FAIXA ESCURA, e ela saiu — 2026-08-14, tarde</h2>
 *
 * <p>Queixa do PO sobre a captura em 1365px: <i>"o frontend não está me
 * agradando"</i>, com duas barras de rolagem circuladas. A faixa não era o que
 * ele circulou, e caiu junto porque a medição mostrou que ela é a outra metade
 * do mesmo defeito:
 *
 * <ul>
 *   <li><b>242,2px de retângulo verde</b> empurrando o primeiro cartão branco
 *       para <b>210,2px abaixo do topo do conteúdo</b> — a página parecia
 *       começar duas vezes;</li>
 *   <li>ela sangrava até a borda da <b>coluna</b> (298,5px) e a lateral escura
 *       acaba em <b>256</b>: <b>42,5px</b> de papel claro entre duas peças
 *       escuras, uma tira cortando a moldura ao meio. Em 1280 a conta dá zero,
 *       e é por isso que o verificador — que para em 1280 — nunca a viu.</li>
 * </ul>
 *
 * <p><b>Escuro é moldura; claro é onde o dado vive</b>, e moldura é o L de
 * cabeçalho + lateral. Uma segunda superfície escura dentro da área de conteúdo
 * ou engorda a moldura ou deixa emenda — não havia terceira saída, e por isso a
 * peça saiu em vez de ser ajustada. O que ela dizia continua dito, na tinta da
 * página.
 *
 * <p>O selo de empresa e o selo de situação continuam na ficha, que é branca:
 * os dois são pastel + borda + texto, medidos contra fundo claro.
 *
 * <h2>🔴 A ficha era um bloco só, e virou DOIS PASSOS — 2026-08-14</h2>
 *
 * <p>Queixa do PO: <i>"não gostei do frontend dessa página — ficou
 * desorganizada eu acho"</i>. Não era tipografia nem cor: era <b>composição</b>,
 * e o que a captura mostrou tem número.
 *
 * <ul>
 *   <li><b>A linha de controles era um vão de 620 px.</b> O campo "Etiquetas"
 *       encostava na margem esquerda do cartão e o botão "Imprimir" na direita
 *       — 619,9 px vazios entre as duas metades de uma decisão só, em 1280;
 *       363,9 px em 768. Hoje o botão é o <b>último</b> item de uma coluna, e
 *       está sempre no mesmo lugar.</li>
 *   <li><b>A ficha orfanava o último dado.</b> Cinco itens num
 *       {@code auto-fit} davam quatro colunas em 1280: "Validade" sozinha numa
 *       segunda fileira com três células vazias ao lado. Hoje são <b>dois
 *       grupos que fecham</b> — 2 e 3 —, e nenhuma largura orfana.</li>
 *   <li>🔴 <b>O que BLOQUEAVA vinha depois do que era bloqueado.</b> Na
 *       reimpressão o botão nascia desabilitado no alto à direita, e o campo
 *       "Motivo" — a única coisa que o destrava — aparecia embaixo e à
 *       esquerda dele. Hoje a ordem é a do ato: quais → por quê →
 *       imprimir.</li>
 *   <li><b>O campo de motivo tinha 942 px para uma frase curta</b> — o mesmo
 *       defeito já corrigido no cadastro de fornecedor, que aqui nunca fora
 *       aplicado.</li>
 *   <li><b>A explicação pesava mais que a ação.</b> O alerta "Ao imprimir"
 *       media 282 px em 360, contra 44 px do botão que ele explica. Virou
 *       {@link Nota}: a pergunta fica na tela, a resposta a um toque.</li>
 * </ul>
 *
 * <h2>🔴 A prévia virou MINIATURA DE FOLHA — #34, 2026-08-14 (noite)</h2>
 *
 * <p>Queixa do PO, olhando a tela em 1365 px: <i>"melhorou muito mas ainda está
 * ruim"</i>, e depois, com o pedido: <i>"a prévia podia aparecer de uma forma
 * menor dentro de uma folha de papel A4 ou A6, em miniatura, e se clicasse
 * expandiria"</i>.
 *
 * <p><b>O que a medição achou tem número</b> (Chrome, sessão real, base de
 * demonstração, lote de 20 tambores):
 *
 * <ul>
 *   <li>a prévia desenhava cada etiqueta a <b>480 × 720 px</b> — que é
 *       <b>127 × 190,5 mm</b>, ou seja <b>maior que a A6 nominal</b> e 44 % mais
 *       área que os 88 × 132 mm que ela realmente imprime;</li>
 *   <li>numa coluna só: {@code repeat(auto-fill, minmax(min(86mm, 100%), 1fr))}
 *       resolve para <b>uma</b> trilha em 480 px, então as vinte empilhavam;</li>
 *   <li>a coluna da folha media <b>14 687 px</b> e a página, <b>15 207 px</b> —
 *       <b>19 telas</b> de rolagem —, enquanto a ficha ao lado acabava em
 *       771 px: <b>~13 900 px de coluna esquerda VAZIA</b> ao lado de uma fita
 *       de etiquetas gigantes.</li>
 * </ul>
 *
 * <p>Nada estourava, nada rolava de lado, nenhum alvo encolhia — <b>o
 * verificador não tem medida para isto</b>. Quem achou foi abrir a captura.
 *
 * <p><b>A prévia não parecia a folha que ela prevê.</b> Hoje ela é a folha: as
 * etiquetas escolhidas são repartidas em <b>folhas A4 de quatro</b>
 * ({@link POR_FOLHA}), cada uma desenhada com a <b>proporção real do papel</b>
 * (210 × 297 mm) e a margem de 8 mm do {@code @page}, em miniatura.
 *
 * <p>⚠️ <b>É O MESMO DESENHO, não um segundo.</b> Cada {@link Etiqueta}
 * aparece <b>uma vez</b> no documento; a folha de papel é só um agrupamento em
 * volta delas, e no papel ela desaparece — {@code display: contents} dentro do
 * {@code @media print} devolve as etiquetas à grade de 88 mm × 2 de sempre.
 * Duas renderizações divergiriam, e a que ninguém olha é a que vai ao papel.
 *
 * <p><b>Clicar amplia</b>, e a decisão está justificada na feature 1.2.1: a
 * folha cresce <b>no lugar</b>, ocupando as duas colunas, em vez de abrir uma
 * camada por cima da tela.
 *
 * <p><b>No telefone ela nasce recolhida.</b> Ver {@link previaNasceAberta} — a
 * pendência que a #36 deixou aberta para o PO, com o número dela (1 579 px →
 * 11 939 px).
 *
 * <p><b>O que NÃO mudou, e é o que importa:</b> o registro da emissão continua
 * acontecendo <b>antes</b> de {@code window.print} — a ordem é o controle, e há
 * teste que falha se alguém a inverter. E a folha continua sendo filha
 * <b>direta</b> de {@code .etiquetas}, que é o que a lista de permitidos do
 * {@code @media print} exige.
 */
export function PaginaEtiquetas() {
  const { id } = useParams<{ id: string }>();
  const [parametros] = useSearchParams();
  const { atual } = usarEmpresas();

  /*
   * A empresa vem da ROTA quando a lista estava consolidada.
   *
   * A tela de lotes deixou de exigir uma empresa escolhida, e esta exige: ela
   * carrega o lote por empresa e IMPRIME documento regulado. Quem sabe de qual
   * empresa é aquele lote é a linha em que se clicou — por isso o link carrega
   * o identificador. Sem isso, "Etiqueta" no consolidado abriria numa tela
   * pedindo para escolher a empresa que a própria linha já sabia.
   */
  const empresaDoLote = parametros.get("empresa") ?? atual?.id ?? null;

  const [lote, definirLote] = useState<Lote | null>(null);
  /*
   * Os recipientes, cada um com o TOKEN DO QR dele.
   *
   * Chamada separada da do lote, e não um campo dele: a lista de lotes carrega
   * dezenas de linhas e não usa volume nenhum — embutir vinte tokens em cada
   * uma faria a tela de conferência pagar por um dado que só esta tela lê.
   */
  const [volumes, definirVolumes] = useState<VolumeDaEtiqueta[]>([]);
  /*
   * QUAIS recipientes vão para o papel — os números, não uma contagem.
   *
   * 🔴 Era `const escolhidos = volumes.slice(0, vias)`: o campo perguntava
   * QUANTAS e a tela pegava as PRIMEIRAS N. Ver o cabeçalho deste arquivo — o
   * defeito custa emissão imutável, não papel.
   */
  const [selecionados, definirSelecionados] = useState<number[]>([]);
  /*
   * A mesma escolha, escrita. `1-3, 7` é a forma que se confere de relance
   * antes de clicar; a grade é a forma que se acerta com o dedo. As duas
   * escrevem no MESMO estado, e é por isso que elas nunca discordam.
   */
  const [expressao, definirExpressao] = useState("");
  const [erroDaFaixa, definirErroDaFaixa] = useState<string | null>(null);
  /*
   * O porquê da reimpressão — restrição regulatória nº 2.
   *
   * Ele só aparece quando algum dos recipientes escolhidos JÁ teve etiqueta, e
   * aí passa a ser obrigatório. Reimprimir é normal — etiqueta rasga, molha e
   * cai atrás do tambor —; o que não pode é reimprimir sem rastro.
   */
  const [motivo, definirMotivo] = useState("");
  /*
   * 🔴 A prévia ABRE VISÍVEL — reversão consciente do PO na #36.
   *
   * Na #34 ele a pediu escondida, e foi entregue assim. Na #36 ele pediu o
   * contrário, olhando a mesma tela dentro das abas: com a conferência à
   * esquerda e a folha à direita, "como vai sair" deixou de ser uma pergunta
   * que se faz depois e passou a ser parte da conferência. A decisão da #34
   * NÃO foi apagada — ela está registrada na feature 1.2.1, com o que mudou
   * entre as duas.
   *
   * ⚠️ O BOTÃO CONTINUA, e a razão é a mesma que fez a prévia ser escondida
   * uma vez: desenhar vinte etiquetas A6 com QR é a peça mais pesada da tela.
   * Quem precisar do espaço a recolhe; o que mudou é o lado padrão.
   *
   * ⚠️ E ela continua sendo ESCONDIDA, nunca removida: a folha é o que vai
   * para a impressora. Trocar isto por `previaAberta && <folha>` faria
   * "imprimir com a prévia recolhida" mandar uma folha VAZIA para o papel com
   * as emissões já gravadas — ver a regra de `@media screen` em `app.css` e o
   * teste que a lê do disco.
   */
  const [previaAberta, definirPreviaAberta] = useState(previaNasceAberta);
  /*
   * 🔴 A folha AMPLIADA — o "se clicasse expandiria" do PO.
   *
   * Em miniatura a folha responde "quantas folhas, e o que cai em cada uma";
   * ampliada ela responde "o que está escrito nesta". São duas perguntas, e a
   * segunda só se faz de vez em quando — por isso o padrão é a miniatura.
   */
  const [ampliada, definirAmpliada] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    if (empresaDoLote === null || id === undefined) return;
    producao
      .lote(empresaDoLote, id)
      .then(definirLote)
      .catch(() => definirErro("Não foi possível carregar o lote."));
    producao
      .volumesDoLote(empresaDoLote, id)
      .then((lista) => {
        definirVolumes(lista);
        /*
         * A tela abre com os que AINDA NÃO TÊM ETIQUETA — e nunca com uma
         * reimpressão armada.
         *
         * Quem chega aqui logo depois de receber o material quer os vinte, e
         * eles são exatamente os vinte sem via emitida: um toque a menos no
         * caminho mais comum. Quem chega para reimprimir o 17 encontra a
         * escolha VAZIA quando o lote já saiu inteiro — e escolher um tambor
         * que já tem adesivo passa a ser um ato, não um padrão. Reimpressão
         * exige motivo, e motivo pré-armado é motivo que se preenche no
         * automático.
         */
        const novos = lista.filter((v) => v.viasEmitidas === 0).map((v) => v.numero);
        definirSelecionados(novos);
        definirExpressao(escreverEscolha(novos));
      })
      /*
       * 🔴 Falha aqui PASSOU A IMPEDIR a etiqueta de sair, e a inversão é a
       * tarefa #25.
       *
       * Antes ela saía sem QR: o argumento era que nome, lote, empresa e
       * validade já identificam o material. O argumento continua verdadeiro e
       * deixou de bastar — a EMISSÃO é registro do recipiente, e sem saber qual
       * recipiente é não há o que registrar. Imprimir assim seria produzir papel
       * sem rastro, que é exatamente o que a emissão existe para impedir.
       *
       * Na prática isto é inalcançável: a V9 conferiu que todo lote tem os
       * volumes dele. O caminho fica porque falha de rede existe.
       */
      .catch(() => definirVolumes([]));
  }, [empresaDoLote, id]);

  if (empresaDoLote === null) {
    return <Alerta tom="atencao">Escolha a empresa para ver a etiqueta.</Alerta>;
  }
  if (erro !== null && lote === null) {
    return <Alerta tom="erro">{erro}</Alerta>;
  }
  if (lote === null) {
    return <p role="status">Carregando…</p>;
  }

  /*
   * O que vai para o papel sai da lista de VOLUMES filtrada pela escolha, e
   * não da escolha em si. A garantia é estrutural: um número que não
   * corresponda a um recipiente registrado não vira etiqueta nem emissão, e
   * nenhuma etiqueta nasce sem o QR daquele tambor.
   */
  const escolhidos = volumes.filter((v) => selecionados.includes(v.numero));
  const numerosEscolhidos = escolhidos.map((v) => v.numero);
  /*
   * Reimpressão é decidida pelo SERVIDOR, não por esta tela: `viasEmitidas` vem
   * de quantas etiquetas daquele recipiente já saíram. A tela só precisa saber
   * disso antes do clique — descobrir que falta o motivo depois de a caixa de
   * impressão abrir é descobrir tarde.
   *
   * 🔴 E o motivo vale POR VOLUME JÁ EMITIDO, não pela folha: escolher o 17
   * (novo) e o 3 (que já saiu) exige motivo por causa do 3, e só dele.
   */
  const reimpressos = escolhidos.filter((v) => v.viasEmitidas > 0);
  const exigeMotivo = reimpressos.length > 0;
  const semVolumes = volumes.length === 0;
  const semEtiqueta = volumes.filter((v) => v.viasEmitidas === 0);
  const podeImprimir =
    !semVolumes && escolhidos.length > 0 && (!exigeMotivo || motivo.trim() !== "");

  /** A escolha mudou pelo DEDO — a expressão acompanha. */
  function escolher(numeros: readonly number[]) {
    const ordenados = [...numeros].sort((a, b) => a - b);
    definirSelecionados(ordenados);
    definirExpressao(escreverEscolha(ordenados));
    definirErroDaFaixa(null);
  }

  function alternar(numero: number) {
    escolher(
      selecionados.includes(numero)
        ? selecionados.filter((n) => n !== numero)
        : [...selecionados, numero],
    );
  }

  /**
   * A escolha mudou pelo TECLADO.
   *
   * <p>O que não se entende **não muda a escolha**: a tela guarda o que estava
   * marcado e diz o que não entendeu. Adivinhar aqui seria escolher tambor por
   * conta própria numa tela que emite documento regulado.
   */
  function digitar(texto: string) {
    definirExpressao(texto);
    const lida = interpretarEscolha(texto, volumes.length);
    definirErroDaFaixa(lida.erro);
    if (lida.erro === null) definirSelecionados(lida.numeros);
  }

  async function imprimir() {
    // Registra ANTES de abrir a caixa de impressão: se o registro falhar, a
    // etiqueta não sai. É o mesmo princípio da trilha de auditoria — o sistema
    // prefere recusar a agir sem deixar rastro.
    //
    // ⚠️ A ORDEM DESTAS DUAS LINHAS É O CONTROLE. Invertê-las produziria papel
    // impresso sem registro, e ninguém perceberia — há teste que falha se
    // `window.print` for chamado sem o registro ter passado.
    if (semVolumes) {
      definirErro(
        "Não foi possível carregar os volumes deste lote — a etiqueta não foi gerada. " +
          "Sem saber qual recipiente é, não há o que registrar.",
      );
      return;
    }
    if (numerosEscolhidos.length === 0) {
      definirErro("Escolha ao menos um volume — nenhuma etiqueta foi gerada.");
      return;
    }
    if (exigeMotivo && motivo.trim() === "") {
      definirErro("Informe o motivo da reimpressão — reimprimir é normal; reimprimir sem rastro, não.");
      return;
    }
    try {
      // 🔴 A EMPRESA VAI JUNTO desde a #27, e é a mesma que carregou este lote —
      // a da rota, não a do seletor do cabeçalho. O servidor a confere contra a
      // do lote e recusa quando divergem: etiqueta emitida pela empresa errada
      // é documento inválido colado num tambor, e a trilha registraria
      // fielmente que ele saiu.
      await producao.registrarImpressao(empresaDoLote!, lote!.id, numerosEscolhidos, motivo.trim());
    } catch {
      definirErro("Não foi possível registrar a impressão — a etiqueta não foi gerada.");
      return;
    }
    definirErro(null);
    window.print();
  }

  const comEtiqueta = volumes.filter((v) => v.viasEmitidas > 0).length;
  /*
   * As folhas de papel. É agrupamento, não desenho: cada etiqueta continua
   * aparecendo UMA vez no documento, e no papel o agrupamento some
   * (`display: contents`). Ver `repartirEmFolhas`.
   */
  const folhas = repartirEmFolhas(escolhidos);

  return (
    <div className={`etiquetas${ampliada ? " etiquetas--ampliada" : ""}`}>
      {/* ── 1. a ficha ───────────────────────────────────────────────────
          🔴 A CABEÇA DE TELA SAIU DAQUI NA #36, e subiu um nível.

          Ela era chapéu + `h1` ("Etiquetas do lote MP-…") + nome do material
          + "← Voltar aos lotes". Hoje quem diz de qual lote é esta tela é o
          cabeçalho do LOTE, acima das abas — código, empresa e situação, em
          toda aba. Repeti-lo aqui daria dois títulos e dois "voltar" na mesma
          página, e um `h1` por aba faria a tela mudar de nome ao trocar de
          seção.

          ⚠️ Isto NÃO afrouxou nada no papel: quem esconde o que não é a folha
          continua sendo a lista de PERMITIDOS do `@media print`, e ela ganhou
          um degrau acima (`.lote-abas`) na mesma edição — bloco novo nasce
          escondido dos dois lados. Ver `app.css`. */}
      <section className="etiquetas__emissao" aria-labelledby="etiquetas-emissao">
        <Cartao>
          <h2 id="etiquetas-emissao">O que vai para o papel</h2>

          {/* 🔴 OS DOIS SELOS — empresa e situação — SUBIRAM PARA O CABEÇALHO
              DO LOTE na #36, e não sumiram: eles agora aparecem em TODA aba,
              acima da lista, em vez de só nesta.

              Eles são as duas coisas que podem FAZER PARAR — material do CNPJ
              errado e material que ainda não foi liberado —, e é exatamente
              por isso que o lugar deles é o cabeçalho do registro e não o
              corpo de uma seção. ADR-0011 §4 continua atendido: a situação
              está na tela, à vista, antes de imprimir. ADR-0005 §10 também: a
              empresa é a DO LOTE, resolvida no servidor.

              Duplicá-los aqui poria o mesmo selo duas vezes na mesma tela, a
              meia dobra um do outro — e dois selos iguais ensinam a duvidar de
              qual dos dois é o atual. */}

          {/* ── O QUE VAI ESCRITO ──────────────────────────────────────── */}
          <dl className="etiquetas__conferencia">
            <div>
              <dt>Matéria-prima</dt>
              <dd className="etiquetas__valor">{lote.codigoMateriaPrima}</dd>
            </div>

            <div>
              <dt>Fornecedor</dt>
              <dd className="etiquetas__valor">{lote.fornecedor}</dd>
            </div>

            {/* Vencido sai com a palavra ao lado da cor, como na tabela de
                lotes: cor sozinha aqui seria o mesmo defeito que o selo
                evita. */}
            <div>
              <dt>Validade</dt>
              <dd className={lote.vencido ? "etiquetas__vencido" : "etiquetas__valor"}>
                {formatar(lote.validade)}
                {lote.vencido && " · vencido"}
              </dd>
            </div>
          </dl>

          {/* ── QUANTO DESTE LOTE JÁ TEM ETIQUETA ───────────────────────
              🔴 Pedido do PO na #36: ver isto **sem trocar de aba**.

              É uma FRASE, e não um par rótulo/valor numa quarta célula da
              grade acima: a grade tem `auto-fit`, e um quarto item orfana
              numa largura em que os três fecham (foi assim que "Validade"
              caiu sozinha com três células vazias ao lado, em 2026-08-14).
              E o que se responde aqui não é um atributo do lote — é o estado
              da rotulagem dele, que muda a cada folha impressa.

              A conta é a mesma da aba `Volumes` (`viasEmitidas > 0`) porque a
              pergunta é a mesma; o que difere é o enquadramento, e por isso
              as frases não são a mesma frase. */}
          <p className="etiquetas__etiquetados">
            {comEtiqueta === 0 ? (
              <>
                <strong>Nenhum</strong> dos {volumes.length}{" "}
                {volumes.length === 1 ? "volume" : "volumes"} tem etiqueta
                emitida.
              </>
            ) : (
              <>
                <strong>
                  {comEtiqueta} de {volumes.length}
                </strong>{" "}
                {volumes.length === 1 ? "volume já tem" : "volumes já têm"}{" "}
                etiqueta emitida — reimprimir {comEtiqueta === 1 ? "esse" : "esses"}{" "}
                exige motivo.
              </>
            )}
          </p>

          {/* ── A EMISSÃO — o segundo passo, e ele é uma COLUNA ──────────
              🔴 A ordem aqui é a do ato, e ela estava invertida: quais →
              por quê (quando for o caso) → imprimir. Antes o botão nascia
              desabilitado no canto superior direito e o campo que o destrava
              aparecia ABAIXO dele — a tela pedia a decisão antes de oferecer
              o que a permite.

              O botão é o ÚLTIMO item, sempre, com ou sem motivo. Lugar fixo
              para a ação é o que dispensa procurá-la a cada estado. */}
          <section className="etiquetas__acao" aria-labelledby="etiquetas-acao">
            <h3 className="etiquetas__passo" id="etiquetas-acao">Emissão</h3>

            {/* ⚠️ Sem volumes carregados não há escolha a oferecer: o que
                aparece é o alerta lá embaixo, e o botão não age. Oferecer uma
                grade vazia e um atalho "Todos os 0" seria a tela fingindo que
                o problema é da pessoa. */}
            {!semVolumes && (
              <div className="etiquetas__escolha">
                {/* ── A ESCOLHA POR ESCRITO ────────────────────────────
                    O modelo mental é o da caixa de impressão do navegador —
                    "páginas: 3-7" —, e é o caminho mais curto de quem está
                    na mesa com o teclado. No celular quem manda é a grade.

                    A dica diz quantos recipientes o lote tem: o teto não é
                    mais um limite de campo numérico, é um fato do lote, e
                    quem digita 99 precisa saber contra o que errou. */}
                <div className="ui-campo etiquetas__faixa">
                  <label className="ui-campo__rotulo" htmlFor="quais-volumes">
                    Quais volumes
                  </label>
                  <input
                    id="quais-volumes"
                    className={`ui-campo__entrada${erroDaFaixa !== null ? " ui-campo__entrada--erro" : ""}`}
                    type="text"
                    inputMode="numeric"
                    value={expressao}
                    placeholder="17 · 3-7 · 1, 5, 9-12"
                    aria-invalid={erroDaFaixa !== null ? true : undefined}
                    aria-describedby={erroDaFaixa !== null ? "quais-erro" : "quais-dica"}
                    onChange={(e) => digitar(e.target.value)}
                  />
                  {erroDaFaixa !== null ? (
                    <p className="ui-campo__erro" id="quais-erro" role="alert">
                      {erroDaFaixa} A escolha não mudou.
                    </p>
                  ) : (
                    <p className="ui-campo__dica" id="quais-dica">
                      {/* `volumes.length` e não `lote.volumes`: o teto que a
                          recusa cita é o da LISTA carregada, e dois números
                          diferentes para a mesma pergunta na mesma tela é
                          como se descobre que um deles está errado tarde. */}
                      Um a um ou em faixa — este lote tem {volumes.length}{" "}
                      {volumes.length === 1 ? "recipiente" : "recipientes"}. Cabem
                      4 etiquetas por folha A4.
                    </p>
                  )}
                </div>

                {/* Os atalhos são as três perguntas que se fazem de verdade:
                    "todas", "as que faltam" e "recomeçar". "Só os sem
                    etiqueta" some quando não há nenhum — atalho que não faz
                    nada ensina a desconfiar dos outros. */}
                <div className="etiquetas__atalhos">
                  <Botao
                    aparencia="secundaria"
                    onClick={() => escolher(volumes.map((v) => v.numero))}
                  >
                    Todos os {volumes.length}
                  </Botao>
                  {semEtiqueta.length > 0 && semEtiqueta.length < volumes.length && (
                    <Botao
                      aparencia="secundaria"
                      onClick={() => escolher(semEtiqueta.map((v) => v.numero))}
                    >
                      Só os {semEtiqueta.length} sem etiqueta
                    </Botao>
                  )}
                  <Botao
                    aparencia="secundaria"
                    onClick={() => escolher([])}
                    disabled={selecionados.length === 0}
                  >
                    Limpar
                  </Botao>
                </div>

                {/* ── A GRADE ─────────────────────────────────────────
                    Cada tambor é um alvo de 44px, e a nota miúda diz quantas
                    vias daquele recipiente já saíram — o dado vem de
                    `viasEmitidas`, do servidor, e é ele que decide se o
                    motivo passa a ser obrigatório. */}
                <SeletorMultiplo
                  rotulo="Volumes deste lote"
                  opcoes={volumes.map(opcaoDoVolume)}
                  selecionados={selecionados.map(String)}
                  aoAlternar={(id) => alternar(Number(id))}
                />

                {/* `role="status"`: aqui a região viva se justifica — o texto
                    muda a cada toque, e é a confirmação de quantos adesivos o
                    próximo clique vai gravar. (A `Nota` lá embaixo é o caso
                    oposto: prosa que nunca muda não é estado.) */}
                <p className="etiquetas__resumo" role="status">
                  {escolhidos.length === 0
                    ? "Nenhum volume escolhido — nada será emitido."
                    : `${escolhidos.length} de ${volumes.length} escolhidos${
                        reimpressos.length > 0
                          ? ` · ${reimpressos.length} já ${reimpressos.length === 1 ? "tem" : "têm"} etiqueta`
                          : ""
                      }.`}
                </p>
              </div>
            )}

            {/* ⚠️ O MOTIVO DA REIMPRESSÃO — restrição regulatória nº 2.
                Ele aparece só quando o servidor diz que algum dos recipientes
                escolhidos JÁ teve etiqueta, e aí é obrigatório: o campo, o caso
                de uso e o `check` do banco recusam os três. Reimprimir é normal;
                o que não pode é reimprimir sem rastro.

                🔴 Ele mora ACIMA do botão desde 2026-08-14. Um campo
                obrigatório que aparece depois do controle que ele destrava faz
                a pessoa clicar num botão morto e só então procurar o que
                falta.

                🔴 E ele é POR VOLUME desde a #34: a dica NOMEIA os tambores que
                já saíram, porque "algum dos escolhidos" não diz qual foi. */}
            {exigeMotivo && (
              <div className="ui-campo etiquetas__motivo">
                <label className="ui-campo__rotulo" htmlFor="motivo-reimpressao">
                  Motivo da reimpressão
                </label>
                <input
                  id="motivo-reimpressao"
                  className="ui-campo__entrada"
                  type="text"
                  value={motivo}
                  aria-describedby="motivo-dica"
                  onChange={(e) => definirMotivo(e.target.value)}
                  placeholder="Ex.: adesivo do tambor 7 rasgou no transporte"
                />
                <p className="ui-campo__dica" id="motivo-dica">
                  {reimpressos.length === 1
                    ? `O volume ${reimpressos[0]!.numero} já tem etiqueta emitida.`
                    : `Já têm etiqueta emitida: ${listar(reimpressos.map((v) => v.numero))}.`}{" "}
                  A segunda via fica no rastro com o motivo — sem ele, a etiqueta não sai.
                </p>
              </div>
            )}

            <Botao onClick={() => void imprimir()} disabled={!podeImprimir}>
              Imprimir {rotuloDaQuantidade(escolhidos.length)}
            </Botao>

            {/* O erro de registro aparece AQUI, colado no botão que o
                produziu — e não no topo da tela, onde ele some da vista de
                quem acabou de clicar embaixo. */}
            {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

            {semVolumes && (
              <Alerta tom="erro" titulo="Sem os volumes, não há o que registrar">
                Os recipientes deste lote não foram carregados. Cada etiqueta
                identifica <strong>um volume</strong>, e a emissão é registrada
                contra ele — sem isso, imprimir produziria papel sem rastro.
              </Alerta>
            )}

            {/* "O que acontece ao imprimir", dito antes de acontecer — e o
                resumo É a pergunta que a pessoa tem com o dedo sobre o botão.

                🔴 Era um `Alerta tom="informacao"` permanente: 282 px de bloco
                azul em 360, contra 44 px do botão que ele explica. `Alerta` diz
                ESTADO e é região viva; prosa que nunca muda dentro de um
                `role="status"` é ruído para leitor de tela. Nada foi cortado —
                o texto está inteiro a um toque. */}
            <Nota resumo="O que acontece ao imprimir?">
              O sistema registra a emissão <strong>antes</strong> de abrir a caixa
              do navegador: se o registro falhar, a etiqueta não sai. É{" "}
              <strong>uma emissão por adesivo</strong> — qual volume, qual via,
              quem, quando —, e cada uma guarda a <strong>cópia do que foi para o
              papel</strong>, para que corrigir o peso amanhã não reescreva a
              etiqueta que já está colada no tambor. Só os volumes{" "}
              <strong>escolhidos</strong> viram emissão, e emissão não se apaga.
            </Nota>
          </section>
        </Cartao>
      </section>

      {/* ── 3. a prévia ──────────────────────────────────────────────────
          O rótulo é irmão da folha, e não um bloco que a embrulhe: o
          `@media print` esconde todo filho de `.etiquetas` que não seja a
          folha, e embrulhada ela sairia escondida junto com o rótulo. */}
      {/* 🔴 ELA ABRE VISÍVEL — reversão consciente do PO na #36.

          Na #34 ele a pediu SOB DEMANDA, e a tela era uma coluna só: a prévia
          ficava no fim da página, longe da escolha, e mostrar vinte etiquetas
          A6 com QR antes de alguém escolher quais quer era peso sem resposta.
          Aquela decisão estava certa para AQUELA tela.

          Na #36 a tela virou duas colunas no PC — conferir e escolher à
          esquerda, a folha à direita —, e a prévia deixou de ser um segundo
          assunto: marcar o tambor 17 e ver a etiqueta do 17 aparecer ao lado é
          uma leitura só. Por isso ela abre aberta, e o botão de RECOLHER
          continua — quem precisar do espaço, ou tiver vinte etiquetas para
          desenhar, a fecha.

          ⚠️ Recolhida, ela continua NO DOCUMENTO — quem a esconde é uma regra
          de `@media screen`. É a folha que vai para a impressora: removê-la
          do JSX faria "imprimir com a prévia recolhida" mandar papel em
          branco com as emissões já gravadas. */}
      <header className="etiquetas__previa">
        <h2>Prévia da folha</h2>
        <p className="etiquetas__previa-apoio">
          É o que sai no papel — A6 retrato (100 × 150 mm),{" "}
          {rotuloDaQuantidade(escolhidos.length)} em{" "}
          {folhas.length === 1 ? "1 folha A4" : `${folhas.length} folhas A4`},
          quatro por folha. Só as etiquetas são impressas.
        </p>
        {/* Dito onde se confere, e não só na documentação: cada etiqueta
            carrega o QR DAQUELE recipiente. Quem imprime precisa saber que
            elas não são intercambiáveis — colar a do volume 3 no tambor 7
            faz o QR responder pelo tambor errado. */}
        <p className="etiquetas__previa-apoio">
          Cada etiqueta traz o QR <strong>do volume dela</strong>: elas{" "}
          <strong>não são intercambiáveis</strong>.
        </p>
        <div className="etiquetas__previa-acoes">
          <Botao
            aparencia="secundaria"
            aria-expanded={previaAberta}
            aria-controls="etiquetas-folha"
            disabled={escolhidos.length === 0}
            onClick={() => definirPreviaAberta(!previaAberta)}
          >
            {previaAberta
              ? "Ocultar prévia"
              : `Ver prévia — ${rotuloDaQuantidade(escolhidos.length)}`}
          </Botao>

          {/* 🔴 O "se clicasse expandiria" também tem BOTÃO, e não só o clique
              na folha: o clique é do ponteiro, e quem percorre a tela pelo
              teclado precisa de um controle rotulado no fluxo do TAB. Os dois
              acionam o mesmo estado. */}
          <Botao
            aparencia="secundaria"
            aria-controls="etiquetas-folha"
            disabled={!previaAberta || escolhidos.length === 0}
            onClick={() => definirAmpliada(!ampliada)}
          >
            {ampliada ? "Reduzir folha" : "Ampliar folha"}
          </Botao>
        </div>
      </header>

      <div
        id="etiquetas-folha"
        className={
          "etiquetas__folha"
          + (previaAberta ? "" : " etiquetas__folha--recolhida")
          + (ampliada ? " etiquetas__folha--ampliada" : "")
        }
      >
        {/*
          Uma etiqueta por RECIPIENTE ESCOLHIDO, e não por índice de laço.

          🔴 A folha deixou de poder ter etiqueta sem volume. Antes,
          `Array.from({ length: vias })` passava `undefined` quando a contagem
          ultrapassava os recipientes existentes, e saía papel sem QR e sem
          número — identificação de material que não diz qual material. Hoje a
          origem da lista é a dos volumes: o que não tem recipiente não vira
          etiqueta.

          🔴 E ela deixou de começar no volume 1. `escolhidos` é a lista de
          recipientes MARCADOS — o mesmo array que vai para o servidor no
          registro da emissão. É a garantia de que o papel e o rastro dizem a
          mesma coisa: não há caminho em que uma etiqueta seja desenhada sem
          que a emissão dela tenha sido registrada.
        */}
        {/* ── AS FOLHAS DE PAPEL ──────────────────────────────────────────
            🔴 A prévia desenhava as etiquetas soltas, em TAMANHO REAL: em
            1365 px cada uma tinha 480 × 720 px (127 × 190,5 mm — maior que a
            A6 nominal), numa coluna só, e a folha media 14 687 px. Vinte
            tambores viravam uma parede, com ~13 900 px de coluna vazia ao
            lado.

            Hoje o que se vê é o PAPEL: folhas A4 na proporção real
            (210 × 297 mm), com a margem de 8 mm do `@page`, quatro etiquetas
            em cada, e o número da folha no canto.

            ⚠️ ESTE `<div>` NÃO EXISTE NO PAPEL. `@media print` o torna
            `display: contents`, e as etiquetas voltam a ser filhas diretas da
            grade de 88 mm × 2 — a MESMA que a impressora sempre recebeu. É por
            isso que a miniatura não pode divergir da folha impressa: não há
            duas renderizações, há uma, com um agrupamento por cima que some. */}
        {folhas.map((daFolha, indice) => (
          <div className="etiquetas__pagina" key={daFolha[0]!.token}>
            {/* O alvo de ampliar cobre a folha inteira — "se clicasse
                expandiria". Ele é `<button>` de verdade, com nome acessível
                próprio, porque um `<div onClick>` não entra no TAB e não se
                anuncia. Dentro dele só o número da folha, que é a paginação
                visível que a miniatura precisa ter. */}
            <button
              type="button"
              className="etiquetas__lupa"
              aria-controls="etiquetas-folha"
              aria-label={`${ampliada ? "Reduzir" : "Ampliar"} — folha ${indice + 1} de ${folhas.length}`}
              onClick={() => definirAmpliada(!ampliada)}
            >
              <span className="etiquetas__numero" aria-hidden="true">
                {indice + 1}/{folhas.length}
              </span>
            </button>
            {daFolha.map((volume) => (
              <Etiqueta key={volume.token} lote={lote} empresa={lote.empresa} volume={volume} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Um tambor na grade — o número grande, e o que ele tem de diferente embaixo.
 *
 * <p>A nota é o que o servidor sabe e a pessoa não: **quantas etiquetas daquele
 * recipiente já saíram**. É o dado que transforma um clique numa reimpressão, e
 * ele precisa estar visível no momento da escolha, não depois.
 *
 * <p>O nome acessível traz a palavra "volume" porque `17` sozinho não diz nada
 * num leitor de tela — e traz a contagem de vias porque ela é a diferença entre
 * marcar um tambor novo e armar uma segunda via.
 */
function opcaoDoVolume(volume: VolumeDaEtiqueta): OpcaoDoSeletor {
  const vias =
    volume.viasEmitidas === 1 ? "1 via" : `${volume.viasEmitidas} vias`;
  return {
    id: String(volume.numero),
    rotulo: String(volume.numero),
    nota: volume.viasEmitidas > 0 ? vias : undefined,
    descricao:
      volume.viasEmitidas > 0
        ? `Volume ${volume.numero} · ${vias} já emitida${volume.viasEmitidas === 1 ? "" : "s"}`
        : `Volume ${volume.numero} · sem etiqueta emitida`,
  };
}

/**
 * "3, 7 e 12" — e nunca uma lista sem fim.
 *
 * <p>A partir de oito ela conta o resto em vez de continuar: a frase existe
 * para a pessoa reconhecer os tambores, e uma enumeração de quinze números
 * dentro de uma dica não se lê — ela se pula, junto com o motivo que estava
 * escrito ao lado.
 */
function listar(numeros: readonly number[]): string {
  const TETO = 8;
  const mostrados = numeros.slice(0, TETO).map(String);
  const resto = numeros.length - mostrados.length;
  const inicio = mostrados.slice(0, -1).join(", ");
  const fim = mostrados[mostrados.length - 1] ?? "";
  const frase = inicio === "" ? fim : `${inicio} e ${fim}`;
  return resto > 0 ? `${mostrados.join(", ")} e mais ${resto}` : frase;
}

/**
 * "1 etiqueta" · "20 etiquetas" — e nunca mais "1 etiqueta(s)".
 *
 * <p>O parêntese não é preciosismo de redação: ele aparece no rótulo do botão
 * que emite documento regulado, e um sistema que não sabe concordar o próprio
 * plural parece um rascunho justo na tela em que a pessoa decide imprimir vinte
 * adesivos.
 */
function rotuloDaQuantidade(quantas: number): string {
  return quantas === 1 ? "1 etiqueta" : `${quantas} etiquetas`;
}

/** Data do calendário, formatada sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
